import React, { useState, useEffect } from "react";
import {
  BarChart3,
  TrendingUp,
  Activity,
  Users,
  MessageSquare,
  Clock,
  Star,
  Award,
  Calendar,
  Eye,
  Hash,
  Zap,
  Target,
  BookOpen,
} from "lucide-react";
import "./styles/index.css";

const CommunityInsights = ({
  community,
  detailed = false,
  analytics,
  trendingTopics,
  previewData,
}) => {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadInsights();
  }, [analytics, previewData, detailed]);

  const loadInsights = () => {
    try {
      setLoading(true);

      // Use real analytics data from the backend API
      const analyticsData = analytics || previewData?.analytics;

      if (analyticsData) {
        const realInsights = [
          {
            id: 1,
            title: "Engagement Rate",
            value: `${
              analyticsData.engagementRate?.percentage ||
              analyticsData.memberEngagement?.rate ||
              "0"
            }%`,
            change: analyticsData.memberEngagement?.change || "0%",
            trend: analyticsData.memberEngagement?.change?.includes("+")
              ? "positive"
              : "neutral",
            description:
              analyticsData.engagementRate?.description ||
              analyticsData.memberEngagement?.description ||
              "No engagement data available",
            icon: Activity,
            color: "#4caf50",
          },
          {
            id: 2,
            title: "Knowledge Sharing",
            value: `${analyticsData.knowledgeSharing?.percentage || "0"}%`,
            change: "0%", // Backend doesn't provide change for this metric
            trend: "neutral",
            description:
              analyticsData.knowledgeSharing?.description ||
              "No knowledge sharing data available",
            icon: BookOpen,
            color: "#2196f3",
          },
          {
            id: 3,
            title: "Response Time",
            value: analyticsData.responseTime?.hours
              ? `${analyticsData.responseTime.hours}h`
              : "0h",
            change: analyticsData.responseTime?.change || "0%",
            trend: analyticsData.responseTime?.change?.includes("-")
              ? "positive"
              : "neutral",
            description:
              analyticsData.responseTime?.description ||
              "No response time data available",
            icon: Clock,
            color: "#ff9800",
          },
          {
            id: 4,
            title: "Member Growth",
            value: analyticsData.memberGrowth?.change || "0%",
            change: "0%", // This field already represents the change
            trend: analyticsData.memberGrowth?.change?.includes("+")
              ? "positive"
              : "neutral",
            description:
              analyticsData.memberGrowth?.description ||
              "No growth data available",
            icon: TrendingUp,
            color: "#9c27b0",
          },
        ];

        setInsights(realInsights);
      } else {
        // No analytics data available - show empty state
setInsights([]);
      }
    } catch (error) {
setInsights([]);
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (trend) => {
    if (trend === "positive") return <TrendingUp size={12} />;
    if (trend === "negative")
      return <TrendingUp size={12} className="rotate-180" />;
    return <TrendingUp size={12} className="opacity-50" />;
  };

  const getTrendColor = (trend) => {
    if (trend === "positive") return "#4caf50";
    if (trend === "negative") return "#f44336";
    return "#9e9e9e";
  };

  if (loading) {
    return (
      <div className="community-preview-content-card">
        <h3>
          <BarChart3 size={20} />
          Community Insights
        </h3>
        <div className="community-preview-insights-loading">
          <div className="loading-spinner"></div>
          <p>Loading insights...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="community-insights">
      <div className="community-preview-content-card">
        <h3>
          <BarChart3 size={20} />
          {detailed ? "Detailed Community Analytics" : "Community Insights"}
        </h3>

        <div className="community-preview-insights-grid">
          {insights.map((insight) => (
            <div
              key={insight.id}
              className="community-preview-insight-card"
              style={{ borderLeft: `4px solid ${insight.color}` }}
            >
              <div className="community-preview-insight-header">
                <div
                  className="community-preview-insight-icon"
                  style={{
                    backgroundColor: `${insight.color}15`,
                    color: insight.color,
                  }}
                >
                  <insight.icon size={20} />
                </div>

                <div className="community-preview-insight-title-section">
                  <h4 className="community-preview-insight-title">
                    {insight.title}
                  </h4>
                  <div className="community-preview-insight-value-row">
                    <span className="community-preview-insight-value">
                      {insight.value}
                    </span>
                    {insight.change !== "0%" && (
                      <span
                        className={`community-preview-insight-change ${insight.trend}`}
                        style={{ color: getTrendColor(insight.trend) }}
                      >
                        {getTrendIcon(insight.trend)}
                        {insight.change}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="community-preview-insight-description">
                {insight.description}
              </div>
            </div>
          ))}
        </div>

        {insights.length === 0 && !loading && (
          <div className="community-preview-no-insights">
            <BarChart3 size={48} />
            <h4>No Analytics Data Available</h4>
            <p>
              Analytics data will appear here when available from the backend.
            </p>
          </div>
        )}
      </div>

      {/* Trending Topics Section */}
      {(trendingTopics || previewData?.trendingTopics) && (
        <div className="community-preview-content-card">
          <h3>
            <Hash size={20} />
            Trending Topics
          </h3>

          <div className="community-preview-trending-topics">
            {(trendingTopics || previewData?.trendingTopics || [])
              .slice(0, 6)
              .map((topic, index) => (
                <div key={index} className="community-preview-trending-topic">
                  <div className="community-preview-topic-rank">
                    #{topic.rank || index + 1}
                  </div>
                  <div className="community-preview-topic-content">
                    <span className="community-preview-topic-tag">
                      {topic.tag}
                    </span>
                    <span className="community-preview-topic-count">
                      {topic.postCount} posts
                    </span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Community Rating Section */}
      {previewData?.rating && (
        <div className="community-preview-content-card">
          <h3>
            <Star size={20} />
            Community Rating
          </h3>

          <div className="community-preview-rating-summary">
            <div className="community-preview-rating-score">
              <span className="community-preview-rating-number">
                {previewData.rating.summary.averageOverall.toFixed(1)}
              </span>
              <div className="community-preview-rating-stars">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    size={16}
                    className={
                      star <=
                      Math.round(previewData.rating.summary.averageOverall)
                        ? "filled"
                        : "empty"
                    }
                  />
                ))}
              </div>
              <span className="community-preview-rating-count">
                ({previewData.rating.summary.totalRatings} reviews)
              </span>
            </div>

            <div className="community-preview-rating-breakdown">
              <div className="community-preview-rating-item">
                <span>Discussion Quality</span>
                <span>
                  {previewData.rating.summary.averageDiscussionQuality.toFixed(
                    1
                  )}
                </span>
              </div>
              <div className="community-preview-rating-item">
                <span>Helpfulness</span>
                <span>
                  {previewData.rating.summary.averageHelpfulness.toFixed(1)}
                </span>
              </div>
              <div className="community-preview-rating-item">
                <span>Knowledge Sharing</span>
                <span>
                  {previewData.rating.summary.averageKnowledgeSharing.toFixed(
                    1
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunityInsights;
